import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { DeliveryOrderApi } from "../../../api/DeliveryOrder/DeliveryOrderApi";
import {
  LinkedOrder,
  DeliveryOrderLinkedOrdersApiResponse,
} from "../../../@types/DRO/DeliveryOrderResponse";

interface LinkedDOsModalProps {
  open: boolean;
  onClose: () => void;
  deliveryOrderId: number | null;
  referenceCode?: string;
}

const LinkedDOsModal: React.FC<LinkedDOsModalProps> = ({
  open,
  onClose,
  deliveryOrderId,
  referenceCode,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [linkedOrders, setLinkedOrders] = useState<LinkedOrder[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && deliveryOrderId) {
      fetchLinkedOrders();
    }
  }, [open, deliveryOrderId]);

  const fetchLinkedOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: DeliveryOrderLinkedOrdersApiResponse =
        await DeliveryOrderApi.api.getLinkedDObyId(deliveryOrderId);
      setLinkedOrders(response.data.linkedOrders);
    } catch (error) {
      console.error("Error fetching linked orders:", error);
      setError("Failed to fetch linked orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, "default" | "primary" | "success" | "warning" | "error"> = {
      DRAFT: "default",
      CREATED: "primary",
      COMPLETED: "success",
      IN_TRANSIT: "warning",
      DELIVERED: "success",
      CANCELLED: "error",
    };
    return colorMap[status] || "default";
  };

  const handleRowClick = (orderId: number) => {
    onClose(); 
    navigate(`/delivery-order/details/${orderId}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
          pb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            Linked Delivery Orders
          </Typography>
          {referenceCode && (
            <Typography variant="body2" color="text.secondary">
              Parent Order: {referenceCode}
            </Typography>
          )}
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <Typography color="error">{error}</Typography>
          </Box>
        ) : linkedOrders.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <Typography color="text.secondary">
              No linked delivery orders found
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell>ID</TableCell>
                  <TableCell>Reference Code</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Scheduled Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {linkedOrders.map((order) => (
                  <TableRow
                    key={order.id}
                    hover
                    onClick={() => handleRowClick(order.id)}
                    sx={{ 
                      "&:last-child td, &:last-child th": { border: 0 },
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    <TableCell>{order.id}</TableCell>
                    <TableCell>{order.referenceCode}</TableCell>
                    <TableCell>{order.vendorWarehouseDto?.vendorName || "N/A"}</TableCell>
                    <TableCell>
                      {order.deliveryWareHouseDetails?.deliveryWarehouseName || "N/A"}
                    </TableCell>
                    <TableCell>{order.scheduledDate}</TableCell>
                    <TableCell>
                      <Chip
                        label={order.status.displayName}
                        size="small"
                        color={getStatusColor(order.status.name)}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LinkedDOsModal;